import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import AuthenticatedFileButton from '../../components/AuthenticatedFileButton';
import {
  bodyTextClass, dangerTextButtonClass, formatCurrency, formatCurrencyInput, inputClass, labelTextClass,
  parseCurrencyInput, primaryButtonClass, strongTextClass, surfaceClass,
} from './shared';

type Extrato = { id: string; tipo: string; nomeOriginal: string; valor?: number | null };
type Tipo = {
  tipo: string;
  label: string;
  ajuda: string;
  /** Rótulo do valor comprovado pelo documento; ausente quando o documento não carrega valor. */
  valorLabel?: string;
  botao: string;
};

/**
 * Contrapartida e rendimento não são digitados na tabela da execução financeira:
 * cada valor é informado junto do comprovante que o prova, e a tabela soma.
 */
const tipos: Tipo[] = [
  { tipo: 'CONTA_CORRENTE', label: 'Extratos Bancários da conta corrente', ajuda: 'Anexe os extratos que abrangem todo o período da prestação de contas.', botao: 'Anexar extrato' },
  {
    tipo: 'APLICACAO_FINANCEIRA', label: 'Extrato da aplicação dos recursos no mercado financeiro',
    ajuda: 'Abranger todo o período da prestação de contas (Decreto 325/2024, artigo 56). Informe o rendimento do período mostrado no extrato — use R$ 0,00 se não houve rendimento.',
    valorLabel: 'Rendimento do período (R$)', botao: 'Anexar extrato',
  },
  {
    tipo: 'DEPOSITO_CONTRAPARTIDA', label: 'Contrapartida — comprovante de depósito',
    ajuda: 'Registre cada depósito de recurso próprio feito na conta do convênio, com o comprovante. A soma dos depósitos é a contrapartida da PC.',
    valorLabel: 'Valor depositado (R$)', botao: 'Registrar depósito',
  },
];

type Props = { pcId: string; podeEditar: boolean; onChange?: () => void };

export default function PrestacaoExtratosSection({ pcId, podeEditar, onChange }: Props) {
  const [extratos, setExtratos] = useState<Extrato[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [valores, setValores] = useState<Record<string, string>>({});
  const [fileKey, setFileKey] = useState(0);
  const base = `/prestacao-contas/pcs/${pcId}/extratos`;
  const carregar = async () => { const res = await api.get(base); setExtratos(res.data); };
  useEffect(() => {
    let ativo = true;
    setErro(null); setExtratos([]); setFiles({}); setValores({}); setFileKey(k => k + 1);
    api.get(base).then(res => { if (ativo) setExtratos(res.data); })
      .catch(() => { if (ativo) setErro('Falha ao carregar extratos.'); });
    return () => { ativo = false; };
  }, [base]);
  const anexar = async ({ tipo, valorLabel }: Tipo) => {
    const file = files[tipo];
    if (!file) { setErro('Selecione um arquivo para anexar.'); return; }
    if (valorLabel && !valores[tipo]) { setErro(`Informe o campo "${valorLabel}".`); return; }
    setOcupado(true); setErro(null);
    try {
      const data = new FormData(); data.append('tipo', tipo); data.append('file', file);
      if (valorLabel) data.append('valor', parseCurrencyInput(valores[tipo]).toFixed(2));
      await api.post(base, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFiles({}); setValores({}); setFileKey(k => k + 1); await carregar(); onChange?.();
    } catch (err: any) { setErro(err?.response?.data?.error || 'Falha ao anexar extrato.'); }
    finally { setOcupado(false); }
  };
  const excluir = async (id: string) => {
    if (!confirm('Excluir este documento? O valor informado junto com ele deixa de contar na execução financeira.')) return;
    setOcupado(true); setErro(null);
    try { await api.delete(`${base}/${id}`); await carregar(); onChange?.(); }
    catch (err: any) { setErro(err?.response?.data?.error || 'Falha ao excluir extrato.'); }
    finally { setOcupado(false); }
  };
  return <section className="space-y-4">
    <h2 className={`text-lg font-semibold ${strongTextClass}`}>Comprovantes da execução financeira</h2>
    {erro && <p role="alert" className="text-sm text-red-600 dark:text-red-300">{erro}</p>}
    {tipos.map((t) => {
      const doTipo = extratos.filter(e => e.tipo === t.tipo);
      const total = doTipo.reduce((soma, e) => soma + Number(e.valor || 0), 0);
      return <div key={t.tipo} className={`space-y-3 rounded-xl border p-4 ${surfaceClass}`}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className={`font-medium ${strongTextClass}`}>{t.label}</h3>
          {t.valorLabel && doTipo.length > 0 && <span className={`text-sm font-semibold ${strongTextClass}`}>Total: {formatCurrency(total)}</span>}
        </div>
        <p className={`text-xs ${labelTextClass}`}>{t.ajuda}</p>
        {doTipo.map(e => <div key={e.id} className="flex flex-wrap items-center gap-3">
          {t.valorLabel && <span className={`text-sm font-semibold ${strongTextClass}`}>{e.valor != null ? formatCurrency(e.valor) : 'Sem valor informado'}</span>}
          <AuthenticatedFileButton url={`${base}/${e.id}/view`} fileName={e.nomeOriginal} className={`text-sm underline ${bodyTextClass}`}>{e.nomeOriginal}</AuthenticatedFileButton>
          {podeEditar && <button disabled={ocupado} onClick={() => excluir(e.id)} className={`text-xs ${dangerTextButtonClass}`}>Excluir</button>}
        </div>)}
        {podeEditar && <div className="flex flex-wrap items-end gap-3">
          {t.valorLabel && <label className={`w-48 shrink-0 text-xs ${labelTextClass}`}>{t.valorLabel}
            <input type="text" inputMode="numeric" placeholder="R$ 0,00" aria-label={t.valorLabel} disabled={ocupado}
              className={`${inputClass} mt-1 text-right`} value={valores[t.tipo] ?? ''}
              onChange={e => setValores(prev => ({ ...prev, [t.tipo]: formatCurrencyInput(e.target.value) }))} />
          </label>}
          <input key={`${fileKey}-${t.tipo}`} type="file" aria-label={t.label} disabled={ocupado} className={`min-w-0 text-sm ${bodyTextClass}`} onChange={e => setFiles(prev => ({ ...prev, [t.tipo]: e.target.files?.[0] || null }))} />
          <button disabled={ocupado || !files[t.tipo] || (!!t.valorLabel && !valores[t.tipo])} onClick={() => anexar(t)} className={`rounded-xl px-3 py-2 text-sm disabled:opacity-50 ${primaryButtonClass}`}>{t.botao}</button>
        </div>}
      </div>;
    })}
  </section>;
}
